import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import { useEffect, useId, useMemo } from 'react';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { usePriceForQuantityPackQuantity } from '../../../hooks/usePagedQuantityPacks';
import { useAppSelector } from '../../../reducers';
import { hasProductInProductInventorySelector, productsFromProductInventoryQuantityPackQuantitySelector, productsFromProductInventoryQuantitySelector } from '../../../selectors/ProductInventorySelectors';
import { productForSkuIdSelector, skuIdIsQuantityPackSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import { trackAdditionalUnitsPicker } from '../../../utils/tracking/trackingEventUtils';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import { FlushedTitle } from '../SharedComponents';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SeatsPickerStepper = ({
  additionalUnitSkuId,
  additionalUnitQuantity,
  onChange,
  trackingSource,
  queryParamTerm,
  disableSteppers = false
}) => {
  const isQuantityPack = useAppSelector(state => skuIdIsQuantityPackSelector(state, {
    skuId: additionalUnitSkuId
  }));
  const {
    includedWithProductVersion,
    includedWithQuantityPack,
    quantityPackApiName,
    productVersionSkuId
  } = useAppSelector(state => {
    if (!isQuantityPack) {
      return {
        includedWithProductVersion: 0,
        includedWithQuantityPack: 0,
        quantityPackApiName: undefined,
        productVersionSkuId: additionalUnitSkuId
      };
    } else {
      const quantityPackSku = skuSelector(state, {
        skuId: additionalUnitSkuId
      });
      const {
        includedWithProductVersion: quantityPackIncludedWithProductVersion,
        includedWithQuantityPack: packagedQuantity,
        quantityPackApiName: quantityPackApiNameScope,
        productVersionSkuId: quantityPackProductVersionSkuId
      } = quantityPackSku;
      return {
        includedWithProductVersion: quantityPackIncludedWithProductVersion,
        includedWithQuantityPack: packagedQuantity,
        quantityPackApiName: quantityPackApiNameScope,
        productVersionSkuId: quantityPackProductVersionSkuId
      };
    }
  });
  const {
    apiName,
    productLine,
    productLevel
  } = useAppSelector(state => productForSkuIdSelector(state, {
    skuId: productVersionSkuId
  }));
  const currencyCode = useDisplayedCurrencyCode();
  const ownedQuantity = useAppSelector(state => quantityPackApiName ? productsFromProductInventoryQuantityPackQuantitySelector(state, {
    productIdentifier: {
      apiName
    },
    quantityPackApiName
  }) : productsFromProductInventoryQuantitySelector(state, {
    skuId: additionalUnitSkuId
  }));
  const minPurchasableSeats = ownedQuantity === 0 ? 1 : ownedQuantity + includedWithProductVersion;
  const ownsProduct = useAppSelector(state => hasProductInProductInventorySelector(state, {
    productAttributeFilters: {
      apiName
    }
  }));
  const ownedUnitQuantity = ownsProduct ? minPurchasableSeats : 0;
  useEffect(() => {
    if (additionalUnitQuantity < ownedQuantity) {
      onChange(ownedQuantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalUnitQuantity, ownedQuantity]);
  const {
    quantityPackPrice,
    loading: isLoadingStartingPOPPriceForQPs
  } = usePriceForQuantityPackQuantity({
    productApiName: apiName,
    quantityPackApiName,
    additionalQuantity: includedWithQuantityPack,
    // the packaged quantity which amounts to 1 quantityPack
    queryParamTerm
  });
  const additionalUnitDisplayPrice = quantityPackPrice ? Number(quantityPackPrice.discountedPrice.monthlyPrice) : undefined;
  const roundValue = ({
    target: {
      value
    }
  }) => {
    const roundedValue = parseInt(value, 10);
    let nextAdditionalUnitQuantity = roundedValue < minPurchasableSeats ? minPurchasableSeats : roundedValue;
    nextAdditionalUnitQuantity = nextAdditionalUnitQuantity - includedWithProductVersion;
    onChange(nextAdditionalUnitQuantity);
  };
  const handleSeatsChange = event => {
    if (event.target.value) {
      const oldQuantity = additionalUnitQuantity + includedWithProductVersion;
      const newQuantity = parseInt(event.target.value, 10);
      trackAdditionalUnitsPicker({
        action: `stepper ${newQuantity > oldQuantity ? 'increase' : 'decrease'}`,
        trackingSource,
        productLine,
        productLevel,
        ownsProduct,
        skuId: additionalUnitSkuId,
        ownedUnitQuantity,
        displayedUnitQuantity: newQuantity
      });
    }
    roundValue(event);
  };
  const SeatsPickerStepperTitleId = useId();
  const SeatsPickerStepperSubtextId = useId();
  const ariaDescribedBy = [SeatsPickerStepperTitleId, SeatsPickerStepperSubtextId].join(' ');
  // productLine will be filled in for a la carte hubs and unbundled suite
  // For TB sku, there's no productLine. Use quantityPack's quantityPackApiName to effectively
  // get its productLine

  const productLineKey = useMemo(() => {
    if (quantityPackApiName === 'additional-sales-seats') {
      return 'SALES';
    }
    if (quantityPackApiName === 'additional-service-seats') {
      return 'SERVICE';
    }
    if (quantityPackApiName === 'additional-commerce-seats') {
      return 'COMMERCE';
    }
    return productLine;
  }, [productLine, quantityPackApiName]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(TFlex, {
      direction: "row",
      justify: "between",
      children: [/*#__PURE__*/_jsx(FlushedTitle, {
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: `pricingPage.seatsPicker.select.${productLineKey}`
        })
      }), /*#__PURE__*/_jsx(StepperInput, {
        "aria-label": `pricingPage.seatsPicker.a11y.select.${productLineKey}`,
        "aria-describedby": ariaDescribedBy,
        value: additionalUnitQuantity + includedWithProductVersion,
        min: minPurchasableSeats,
        onChange: handleSeatsChange,
        "data-selenium-class": "seats-picker-input",
        "data-selenium-id": `${trackingSource}-${apiName}-seat-card-stepper-input`,
        disabled: disableSteppers,
        disabledMinButtonTooltip: disableSteppers ? null : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "interstitial.seatsPicker.disabledMinButtonTooltip"
        }),
        disabledMinButtonTooltipProps: {
          placement: 'bottom',
          children: /*#__PURE__*/_jsx(_Fragment, {})
        }
      })]
    }), /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      as: "div",
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        heights: [20],
        widths: [55],
        isLoading: isLoadingStartingPOPPriceForQPs || additionalUnitDisplayPrice === undefined,
        children: /*#__PURE__*/_jsx(TFlex, {
          direction: "column",
          children: /*#__PURE__*/_jsx("span", {
            id: SeatsPickerStepperSubtextId,
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: `pricingPage.seatsPicker.subtext.seats.${productLineKey}_jsx`,
              elements: {
                PriceDisplay
              },
              options: {
                currencyCode,
                price: additionalUnitDisplayPrice
              }
            })
          })
        })
      })
    })]
  });
};
export default SeatsPickerStepper;